import React, { useState, useRef, useEffect } from 'react';
import { Box, Collapse, Paper } from '@mui/material';
import { history } from 'src/utils/history';
import EventService from 'src/services/EventService';
import { ROUTES_URL } from 'src/constants/routes';
import sessionStore from 'src/stores/session';
import userStore from 'src/stores/user';
import restorePasswordStore from 'src/stores/restorePassword';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import Lock from 'src/icons/Lock';
import Mail from 'src/icons/Mail';
import Exit from 'src/icons/Exit';
import ChevroneUp from 'src/icons/ChevroneUp';
import { ProfileIcon } from '../Icons/Profile';
import { AccordionMenuContainer } from './AccordionMenu';
import ItemMenu from './ItemMenu';

interface ProfileDropdownProps {
    email: string;
    bonusPoints: number;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ email, bonusPoints }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobileSize();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleCloseMenu = () => {
            setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            const subscriber = EventService.subscribe('CLOSE_MENU', handleCloseMenu);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                subscriber.unsubscribe();
            };
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleProfileClick = () => {
        history.push(ROUTES_URL.PROFILE);
        EventService.dispatch('CLOSE_MENU');
        setIsOpen(false);
    };

    const handleChangePasswordClick = () => {
        const { email } = userStore.getState();
        restorePasswordStore.getState().setEmail(email);
        history.push(ROUTES_URL.RESTORE_PASSWORD);
        EventService.dispatch('CLOSE_MENU');
        setIsOpen(false);
    };

    const handleChangeEmailClick = () => {
        history.push(ROUTES_URL.CHANGE_EMAIL);
        EventService.dispatch('CLOSE_MENU');
        setIsOpen(false);
    };

    const handleLogout = () => {
        const store = sessionStore.getState();
        EventService.dispatch('CLOSE_MENU');
        history.push(ROUTES_URL.LOGIN);
        store.logout();
        setIsOpen(false);
    };

    return (
        <Box
            ref={dropdownRef}
            sx={{ position: 'relative' }}
        >
            <AccordionMenuContainer mb={1}>
                <ItemMenu
                    icon={<ProfileIcon />}
                    title={email}
                    subtitle={`Бонусы: ${bonusPoints} Б`}
                    action
                    onAction={handleToggle}
                    customActionComponent={
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            height={24}
                            width={24}
                            borderRadius="50%"
                            bgcolor={isOpen ? 'background.paper' : 'background.default'}
                            sx={{
                                transform: !isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transformOrigin: 'center',
                                transition: 'transform 0.2s ease',
                            }}
                        >
                            <ChevroneUp />
                        </Box>
                    }
                />
            </AccordionMenuContainer>
            <Collapse
                in={isOpen}
                sx={{
                    position: 'absolute',
                    ...(isMobile
                        ? {
                              top: '100%',
                              left: 0,
                              right: 0,
                              mt: 1,
                          }
                        : {
                              bottom: '100%',
                              left: 0,
                              right: 0,
                              mb: 1,
                          }),
                    zIndex: 1000,
                }}
            >
                <Paper
                    elevation={4}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        backgroundColor: 'background.paper',
                        py: 1.5,
                    }}
                >
                    <ItemMenu
                        icon={<ProfileIcon />}
                        title="Профиль"
                        onAction={handleProfileClick}
                    />
                    <ItemMenu
                        icon={<Lock />}
                        title="Изменить пароль"
                        onAction={handleChangePasswordClick}
                    />
                    <ItemMenu
                        icon={<Mail />}
                        title="Изменить почту"
                        onAction={handleChangeEmailClick}
                    />
                    <ItemMenu
                        icon={<Exit />}
                        title="Выйти из учётной записи"
                        onAction={handleLogout}
                        customActionComponent={<div />}
                    />
                </Paper>
            </Collapse>
        </Box>
    );
};

export default ProfileDropdown;
